<template>
  <div class="traz-wrap">

    <!-- Cabecera -->
    <div class="header-flex">
      <div>
        <h1>Trazabilidad del sistema</h1>
        <p class="subtitulo">Registro de todas las acciones realizadas por los usuarios.</p>
      </div>
    </div>

    <div class="divider"></div>

    <!-- Filtros -->
    <section class="filtros">
      <div class="f-group">
        <label>Usuario</label>
        <select v-model="filtros.id_usuario">
          <option value="">Todos</option>
          <option v-for="u in opciones.usuarios" :key="u.id" :value="u.id">{{ u.name }}</option>
        </select>
      </div>
      <div class="f-group">
        <label>Entidad</label>
        <select v-model="filtros.entidad">
          <option value="">Todas</option>
          <option v-for="e in opciones.entidades" :key="e" :value="e">{{ e }}</option>
        </select>
      </div>
      <div class="f-group">
        <label>Acción</label>
        <select v-model="filtros.accion">
          <option value="">Todas</option>
          <option v-for="a in opciones.acciones" :key="a" :value="a">{{ etiquetaAccion(a) }}</option>
        </select>
      </div>
      <div class="f-group">
        <label>Proceso (ejecución)</label>
        <input v-model="filtros.id_ejecucion" type="number" min="1" placeholder="ID" />
      </div>
      <div class="f-group">
        <label>Desde</label>
        <input v-model="filtros.fecha_desde" type="date" />
      </div>
      <div class="f-group">
        <label>Hasta</label>
        <input v-model="filtros.fecha_hasta" type="date" />
      </div>
      <div class="f-group f-group--wide">
        <label>Búsqueda</label>
        <input v-model="filtros.q" type="text" placeholder="Descripción o entidad…" @keyup.enter="buscar" />
      </div>
      <div class="f-acciones">
        <button class="btn guardar" @click="buscar" :disabled="cargando">Filtrar</button>
        <button class="btn salir" @click="limpiar" :disabled="cargando">Limpiar</button>
        <button class="btn accion" @click="exportar" :disabled="cargando || !registros.length">Exportar CSV</button>
      </div>
    </section>

    <!-- Tabla -->
    <section class="tabla-wrap">
      <div v-if="cargando" class="estado">Cargando…</div>
      <div v-else-if="!registros.length" class="estado">Sin registros para los filtros seleccionados.</div>
      <table v-else class="tabla">
        <thead>
          <tr>
            <th>Fecha</th>
            <th>Usuario</th>
            <th>Acción</th>
            <th>Entidad</th>
            <th>Descripción</th>
            <th>Proceso</th>
            <th>IP</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="r in registros" :key="r.id">
            <tr>
              <td class="nowrap">{{ fecha(r.created_at) }}</td>
              <td>
                <div class="usuario-celda">
                  <span v-if="rolDe(r)" :class="['rol-badge', claseBadgeRol(rolDe(r))]">{{ rolDe(r) }}</span>
                  <span class="usuario-nombre">{{ r.usuario?.name ?? '—' }}</span>
                </div>
              </td>
              <td><span :class="['pill', `pill--${r.accion}`]">{{ etiquetaAccion(r.accion) }}</span></td>
              <td>{{ r.entidad }} <span v-if="r.entidad_id" class="muted">#{{ r.entidad_id }}</span></td>
              <td>{{ r.descripcion }}</td>
              <td class="nowrap">{{ r.id_ejecucion ? `#${r.id_ejecucion}` : '—' }}</td>
              <td class="nowrap muted">{{ r.ip_address ?? '—' }}</td>
              <td>
                <button
                  v-if="r.valores_anteriores || r.valores_nuevos"
                  class="btn-mini"
                  @click="expandido = expandido === r.id ? null : r.id"
                >
                  {{ expandido === r.id ? 'Ocultar' : 'Ver cambios' }}
                </button>
              </td>
            </tr>
            <tr v-if="expandido === r.id" class="detalle-row">
              <td colspan="8">
                <div class="detalle">
                  <div v-if="r.valores_anteriores" class="detalle-col">
                    <span class="detalle-lbl">Antes</span>
                    <pre>{{ formatear(r.valores_anteriores) }}</pre>
                  </div>
                  <div v-if="r.valores_nuevos" class="detalle-col">
                    <span class="detalle-lbl">Después</span>
                    <pre>{{ formatear(r.valores_nuevos) }}</pre>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </section>

    <!-- Paginación -->
    <footer v-if="!cargando && registros.length" class="paginacion">
      <span>{{ total }} registro(s) · página {{ pagina }} de {{ ultimaPagina }}</span>
      <div class="pag-btns">
        <button class="btn guardar" :disabled="pagina <= 1" @click="irPagina(pagina - 1)">Anterior</button>
        <button class="btn guardar" :disabled="pagina >= ultimaPagina" @click="irPagina(pagina + 1)">Siguiente</button>
      </div>
    </footer>

    <MensajeFlotante
      :visible="mensajeVisible"
      :mensaje="mensajeTexto"
      :tipo="mensajeTipo"
      @cerrar="mensajeVisible = false"
    />
  </div>
</template>

<script lang="ts" setup>
/**
 * Trazabilidad global de todas las acciones del sistema.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `/trazabilidad` (Trazabilidad) — permiso `trazabilidad.ver`
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { onMounted, reactive, ref } from 'vue'
import {
  listarAuditoria,
  opcionesAuditoria,
  exportarAuditoria,
  type RegistroAuditoria,
  type OpcionesFiltro,
} from '@/services/TrazabilidadService'
import MensajeFlotante from '@/components/MensajeFlotante.vue'

const registros = ref<RegistroAuditoria[]>([])
const opciones = ref<OpcionesFiltro>({ entidades: [], acciones: [], usuarios: [] })
const cargando = ref(false)

const mensajeVisible = ref(false)
const mensajeTexto   = ref('')
const mensajeTipo    = ref<'positivo' | 'error'>('error')

function mostrarMensaje(texto: string, tipo: 'positivo' | 'error' = 'error') {
  mensajeTexto.value   = texto
  mensajeTipo.value    = tipo
  mensajeVisible.value = true
}
const pagina = ref(1)
const ultimaPagina = ref(1)
const total = ref(0)
const expandido = ref<number | null>(null)

const filtros = reactive({
  id_usuario: '' as number | string,
  entidad: '',
  accion: '',
  id_ejecucion: '' as number | string,
  fecha_desde: '',
  fecha_hasta: '',
  q: '',
})

function etiquetaAccion(a: string): string {
  return ({ creado: 'Creación', actualizado: 'Actualización', eliminado: 'Eliminación' } as Record<string, string>)[a] || a
}

function rolDe(r: RegistroAuditoria): string {
  const rol = r.usuario?.roles?.[0]
  return rol ? (rol.label ?? rol.name) : ''
}

const PALETA_ROL = [
  'rol-v1', // azul
  'rol-v2', // naranja
  'rol-v3', // verde
  'rol-v4', // teal
  'rol-v5', // violeta
] as const

function claseBadgeRol(nombre: string): string {
  if (!nombre) return PALETA_ROL[0]
  let hash = 0
  for (let i = 0; i < nombre.length; i++) hash = (hash * 31 + nombre.charCodeAt(i)) >>> 0
  return PALETA_ROL[hash % PALETA_ROL.length]
}

function fecha(iso: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return isNaN(d.getTime()) ? iso : d.toLocaleString('es-CO')
}

function formatear(valor: Record<string, unknown> | null): string {
  return valor ? JSON.stringify(valor, null, 2) : ''
}

async function cargar() {
  cargando.value = true
  try {
    const result = await listarAuditoria({ ...filtros, page: pagina.value, per_page: 25 })
    if (result.ok && result.data) {
      registros.value = result.data.data
      pagina.value = result.data.current_page
      ultimaPagina.value = result.data.last_page
      total.value = result.data.total
    } else {
      registros.value = []
      mostrarMensaje(result.error ?? 'No se pudo cargar el registro de trazabilidad.', 'error')
    }
  } finally {
    cargando.value = false
  }
}

function buscar() {
  pagina.value = 1
  cargar()
}

function irPagina(p: number) {
  pagina.value = p
  cargar()
}

function limpiar() {
  filtros.id_usuario = ''
  filtros.entidad = ''
  filtros.accion = ''
  filtros.id_ejecucion = ''
  filtros.fecha_desde = ''
  filtros.fecha_hasta = ''
  filtros.q = ''
  buscar()
}

async function exportar() {
  await exportarAuditoria(filtros, 'csv')
}

onMounted(async () => {
  const r = await opcionesAuditoria()
  if (r.ok && r.data) opciones.value = r.data
  else mostrarMensaje(r.error ?? 'No se pudieron cargar las opciones de filtro.', 'error')
  await cargar()
})
</script>

<style scoped>
/* ── Variables locales ─────────────────────────── */
.traz-wrap {
  --azul:    #0e69a0;
  --azul-cl: #e8f1f8;
  --linea:   #e0e0e0;
  --tinta:   #1e2a35;
  --mute:    #5b6b7a;

  background-color: white;
  padding: var(--space-lg, 1.5rem);
  border-radius: var(--radius-lg, 12px);
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  margin: 20px;
  font-family: var(--font-family, sans-serif);
}

/* ── Cabecera ──────────────────────────────────── */
.header-flex {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

h1 {
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
  color: var(--tinta);
}

.subtitulo {
  margin: 4px 0 0;
  font-size: 0.875rem;
  color: var(--mute);
}

.divider {
  border: none;
  border-bottom: 1px solid var(--linea);
  margin: 1rem 0;
}

/* ── Filtros ───────────────────────────────────── */
.filtros {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  background: #f8f9fa;
  border: 1px solid var(--linea);
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
}

.f-group { display: flex; flex-direction: column; gap: 4px; }
.f-group--wide { flex: 1; min-width: 180px; }
.f-group label {
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.4px; color: var(--mute);
}
.f-group select,
.f-group input {
  padding: 7px 9px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  color: var(--tinta);
  background: #fff;
  min-width: 130px;
  transition: border-color 0.15s;
}
.f-group select:focus,
.f-group input:focus {
  outline: none;
  border-color: var(--azul);
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

.f-acciones { display: flex; gap: 8px; align-items: flex-end; margin-left: auto; }

/* ── Botones estándar ──────────────────────────── */
.btn {
  padding: 0.5rem 1.1rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.88rem;
  font-family: inherit;
  transition: background-color 0.15s, color 0.15s;
  display: inline-flex;
  align-items: center;
  height: 36px;
}
.btn:disabled { opacity: 0.5; cursor: not-allowed; }

.btn.guardar { background-color: #eea222; color: #000; }
.btn.guardar:hover:not(:disabled) { background-color: var(--azul); color: #fff; }

.btn.salir { background-color: #fff; border: 1px solid #ccc; color: #333; }
.btn.salir:hover:not(:disabled) { background-color: #d41a1a; color: #fff; }

.btn.accion { background-color: var(--azul); color: #fff; }
.btn.accion:hover:not(:disabled) { background-color: #0b5580; color: #fff; }

/* ── Tabla ─────────────────────────────────────── */
.tabla-wrap {
  border: 1px solid var(--linea);
  border-radius: 10px;
  overflow-x: auto;
  margin-bottom: 16px;
}

.estado {
  padding: 32px;
  text-align: center;
  color: var(--mute);
  font-style: italic;
}

.tabla { width: 100%; border-collapse: collapse; font-size: 13px; }

.tabla th {
  background: var(--azul-cl);
  color: #0c447c;
  text-align: left;
  padding: 10px 12px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  position: sticky;
  top: 0;
}

.tabla td {
  padding: 9px 12px;
  border-top: 1px solid var(--linea);
  color: var(--tinta);
  vertical-align: top;
}

.nowrap { white-space: nowrap; }
.muted  { color: var(--mute); }

.usuario-celda {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.usuario-nombre { font-size: 13px; color: var(--tinta); }

.rol-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  align-self: flex-start;
}

/* Paleta de colores por rol (determinista vía hash) */
.rol-v1 { background: linear-gradient(135deg, #0e69a0 0%, #1a8dcf 100%); }   /* azul */
.rol-v2 { background: linear-gradient(135deg, #c47a00 0%, #eea222 100%); }   /* naranja */
.rol-v3 { background: linear-gradient(135deg, #1a7a4a 0%, #28a665 100%); }   /* verde */
.rol-v4 { background: linear-gradient(135deg, #0d7a7a 0%, #14a8a8 100%); }   /* teal */
.rol-v5 { background: linear-gradient(135deg, #6b21a8 0%, #9333ea 100%); }   /* violeta */

.pill { display: inline-block; padding: 2px 9px; border-radius: 999px; font-size: 11px; font-weight: 700; }
.pill--creado      { background: #d1fae5; color: #065f46; }
.pill--actualizado { background: #dbeafe; color: #1e40af; }
.pill--eliminado   { background: #fee2e2; color: #991b1b; }

.btn-mini {
  background: none;
  border: 1px solid var(--linea);
  border-radius: 6px;
  padding: 3px 8px;
  cursor: pointer;
  font-size: 12px;
  font-family: inherit;
  color: var(--azul);
  transition: background-color 0.15s;
}
.btn-mini:hover { background-color: var(--azul-cl); }

/* ── Detalle expandido ─────────────────────────── */
.detalle-row td { background: #f7fafc; }
.detalle         { display: flex; gap: 20px; flex-wrap: wrap; }
.detalle-col     { flex: 1; min-width: 240px; }
.detalle-lbl     { font-size: 11px; font-weight: 700; text-transform: uppercase; color: var(--mute); }
.detalle pre {
  margin: 4px 0 0;
  font-size: 12px;
  background: #fff;
  border: 1px solid var(--linea);
  border-radius: 6px;
  padding: 8px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

/* ── Paginación ────────────────────────────────── */
.paginacion {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
  color: var(--mute);
  font-size: 13px;
  flex-wrap: wrap;
  gap: 10px;
}
.pag-btns { display: flex; gap: 8px; }

@media (max-width: 720px) {
  .traz-wrap { min-width: unset; margin: 10px; }
  .f-group select, .f-group input { min-width: 110px; }
}
</style>
